import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  RefreshCw,
  Search,
  SlidersHorizontal,
  TriangleAlert,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { MnoCode, Order, OrderStatus } from "@evocha/shared-types";
import { MNOS } from "@evocha/shared-types";
import { api } from "../lib/api";
import { errorMessage, relativeTime, shortDateTime, useOrders } from "../lib/queries";
import { tzs } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";

const STATUS_FILTERS: Array<{ value: "all" | OrderStatus; label: string }> = [
  { value: "all", label: "All" },
  { value: "SUCCESS", label: "Sent" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Not completed" },
  { value: "MANUAL_REVIEW", label: "Review" },
];

const NETWORKS: Array<{ value: "all" | MnoCode; label: string }> = [
  { value: "all", label: "All networks" },
  { value: "vodacom", label: "Vodacom" },
  { value: "airtel", label: "Airtel" },
  { value: "tigo", label: "Tigo" },
  { value: "halotel", label: "Halotel" },
  { value: "zantel", label: "Zantel" },
  { value: "ttcl", label: "TTCL" },
];

function mnoColour(code: string): string {
  return MNOS[code as MnoCode]?.colour ?? "#64748b";
}

export function Transactions() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState<"all" | OrderStatus>("all");
  const [network, setNetwork] = useState<"all" | MnoCode>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<Array<string | undefined>>([]);
  const [selected, setSelected] = useState<Order | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const params = useMemo(
    () => ({
      status: status === "all" ? undefined : status,
      mno: network === "all" ? undefined : network,
      q: debounced || undefined,
      from: from || undefined,
      to: to || undefined,
      cursor,
      limit: 25,
    }),
    [status, network, debounced, from, to, cursor],
  );

  const orders = useOrders(params);
  const rows = orders.data?.data ?? [];
  const page = orders.data?.page;

  const totals = useMemo(() => {
    const gross = rows.reduce((sum, o) => sum + Number(o.amount_minor), 0);
    const commission = rows.reduce((sum, o) => sum + Number(o.commission_minor), 0);
    return { gross, commission };
  }, [rows]);

  function changeFilter(apply: () => void) {
    setCursor(undefined);
    setHistory([]);
    apply();
  }

  function nextPage() {
    if (!page?.next_cursor) return;
    setHistory((h) => [...h, cursor]);
    setCursor(page.next_cursor);
  }

  function prevPage() {
    setHistory((h) => {
      if (!h.length) return h;
      const copy = [...h];
      setCursor(copy.pop());
      return copy;
    });
  }

  function resetAll() {
    setQuery("");
    setDebounced("");
    setStatus("all");
    setNetwork("all");
    setFrom("");
    setTo("");
    setCursor(undefined);
    setHistory([]);
  }

  async function refreshOrder(ref: string) {
    try {
      const fresh = await api.statusCheck(ref);
      setSelected(fresh);
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["wallet"] });
    } catch {
      /* the drawer keeps showing the last known state */
    }
  }

  return (
    <div className="stack-6">
      <div className="page-head">
        <div>
          <h2 className="ev-h2">Transactions</h2>
          <p className="ev-muted">Every sale, its provider reference and where the money went.</p>
        </div>
        <div className="page-head-actions">
          <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={() => void orders.refetch()} disabled={orders.isFetching}>
            {orders.isFetching ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />} Refresh
          </button>
          <Link to="/sell" className="ev-btn ev-btn-sm ev-btn-primary">
            New sale <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>

      <section className="ev-card tx-toolbar">
        <div className="tx-search">
          <Search size={17} aria-hidden />
          <input
            className="ev-input"
            value={query}
            onChange={(e) => changeFilter(() => setQuery(e.target.value))}
            placeholder="Search by reference or number…"
            aria-label="Search transactions"
          />
          {query && (
            <button className="tx-search-clear" onClick={() => changeFilter(() => setQuery(""))} aria-label="Clear search">
              <X size={15} />
            </button>
          )}
        </div>

        <div className="tx-filter-chips" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className="ev-chip"
              aria-pressed={status === f.value}
              onClick={() => changeFilter(() => setStatus(f.value))}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button className="ev-btn ev-btn-sm ev-btn-secondary tx-filter-toggle" onClick={() => setShowFilters((v) => !v)}>
          <SlidersHorizontal size={15} /> Filters
        </button>
      </section>

      {showFilters && (
        <section className="ev-card ev-card-pad tx-advanced">
          <label className="ev-field">
            <span className="ev-label">Network</span>
            <select className="ev-select" value={network} onChange={(e) => changeFilter(() => setNetwork(e.target.value as "all" | MnoCode))}>
              {NETWORKS.map((n) => (
                <option key={n.value} value={n.value}>{n.label}</option>
              ))}
            </select>
          </label>
          <label className="ev-field">
            <span className="ev-label">From</span>
            <input className="ev-input" type="date" value={from} onChange={(e) => changeFilter(() => setFrom(e.target.value))} />
          </label>
          <label className="ev-field">
            <span className="ev-label">To</span>
            <input className="ev-input" type="date" value={to} onChange={(e) => changeFilter(() => setTo(e.target.value))} />
          </label>
          <div className="tx-advanced-foot">
            <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={resetAll}>Reset</button>
            <button className="ev-btn ev-btn-sm ev-btn-primary" onClick={() => setShowFilters(false)}>Done</button>
          </div>
        </section>
      )}

      <div className="tx-summary">
        <div>
          <span className="ev-eyebrow">On this page</span>
          <strong>{rows.length}</strong>
          <span className="ev-caption">transactions</span>
        </div>
        <div>
          <span className="ev-eyebrow">Gross value</span>
          <strong>{tzs(totals.gross)}</strong>
          <span className="ev-caption">airtime sold</span>
        </div>
        <div>
          <span className="ev-eyebrow">Commission</span>
          <strong className="ev-pos">+{tzs(totals.commission)}</strong>
          <span className="ev-caption">earned on these</span>
        </div>
        <div className="tx-summary-note">
          <CalendarDays size={15} /> {from || to ? `${from || "start"} → ${to || "today"}` : "All time"}
        </div>
      </div>

      <section className="ev-card">
        {orders.isLoading ? (
          <div className="ev-empty">
            <Loader2 size={22} className="spin" />
            <strong>Loading transactions…</strong>
          </div>
        ) : orders.isError ? (
          <div className="ev-empty">
            <TriangleAlert />
            <strong>Could not load transactions</strong>
            <p className="ev-caption">{errorMessage(orders.error)}</p>
            <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={() => void orders.refetch()}>Try again</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="ev-empty">
            <Filter />
            <strong>No transactions match these filters</strong>
            <p className="ev-caption">Try a different status or clear your search.</p>
            <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={resetAll}>Clear filters</button>
          </div>
        ) : (
          <>
            <div className="ev-table-wrap">
              <table className="ev-table tx-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Number</th>
                    <th>Network</th>
                    <th className="ev-num">Amount</th>
                    <th className="ev-num">Commission</th>
                    <th>Status</th>
                    <th className="ev-num">Time</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.ref} onClick={() => setSelected(o)} className="tx-row" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") setSelected(o); }}>
                      <td><span className="ev-mono tx-ref">{o.ref}</span></td>
                      <td><span className="ev-mono">{o.msisdn_masked}</span></td>
                      <td>
                        <span className="network-cell">
                          <span className="mno-dot" style={{ background: mnoColour(o.mno.code) }} />
                          {o.mno.name}
                        </span>
                      </td>
                      <td className="ev-num ev-money">{o.amount_display}</td>
                      <td className="ev-num ev-pos">+{o.commission_display}</td>
                      <td><StatusBadge status={o.status} label={o.status_label} /></td>
                      <td className="ev-num ev-muted" title={shortDateTime(o.created_at)}>{relativeTime(o.created_at)}</td>
                      <td className="ev-num"><ChevronRight size={15} className="tx-chevron" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="tx-foot">
              <span className="ev-caption">
                Showing {rows.length} transaction{rows.length === 1 ? "" : "s"}
                {page?.has_more ? " · more available" : ""}
              </span>
              <div className="pager">
                <button className="ev-icon-btn" disabled={!history.length} onClick={prevPage} aria-label="Previous page"><ChevronLeft size={16} /></button>
                <span className="pager-page">{history.length + 1}</span>
                <button className="ev-icon-btn" disabled={!page?.has_more} onClick={nextPage} aria-label="Next page"><ChevronRight size={16} /></button>
              </div>
            </div>
          </>
        )}
      </section>

      {selected && (
        <OrderDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onRefresh={() => void refreshOrder(selected.ref)}
        />
      )}
    </div>
  );
}

function OrderDrawer({ order, onClose, onRefresh }: { order: Order; onClose: () => void; onRefresh: () => void }) {
  const pending = order.status === "PENDING" || order.status === "MANUAL_REVIEW";
  return (
    <div className="drawer-scrim" role="presentation" onClick={onClose}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={`Transaction ${order.ref}`} onClick={(e) => e.stopPropagation()}>
        <header className="drawer-head">
          <div>
            <span className="ev-eyebrow">Transaction</span>
            <h3 className="ev-h3 ev-mono" style={{ fontSize: 16 }}>{order.ref}</h3>
          </div>
          <button className="ev-icon-btn" onClick={onClose} aria-label="Close details"><X size={18} /></button>
        </header>

        <div className="drawer-amount">
          <span className="ev-caption">Airtime value</span>
          <strong>{order.amount_display}</strong>
          <StatusBadge status={order.status} label={order.status_label} />
        </div>

        {order.message && (
          <div className={`drawer-alert drawer-alert-${pending ? "info" : order.status === "SUCCESS" ? "success" : "danger"}`}>
            <TriangleAlert size={16} />
            <p>{order.message}</p>
          </div>
        )}

        <dl className="drawer-rows">
          <div><dt>Mobile number</dt><dd className="ev-mono">{order.msisdn_masked}</dd></div>
          <div><dt>Network</dt><dd>{order.mno.name}</dd></div>
          <div><dt>Commission</dt><dd className="ev-pos">+{order.commission_display}</dd></div>
          <div><dt>Service fee</dt><dd>{tzs(order.fee_minor)}</dd></div>
          <div><dt>Provider</dt><dd className="ev-capitalize">{order.provider ?? "mock"}</dd></div>
          <div><dt>Channel</dt><dd className="ev-capitalize">{order.channel ?? "web"}</dd></div>
          <div><dt>Created</dt><dd>{shortDateTime(order.created_at)}</dd></div>
          {order.completed_at && <div><dt>Completed</dt><dd>{shortDateTime(order.completed_at)}</dd></div>}
          {order.unknown_since && <div><dt>Unconfirmed since</dt><dd>{shortDateTime(order.unknown_since)}</dd></div>}
          {order.balance_after && (
            <div><dt>Balance after</dt><dd>{tzs(order.balance_after.available_minor)}</dd></div>
          )}
        </dl>

        <div className="drawer-timeline">
          <span className="ev-eyebrow">Timeline</span>
          <ol>
            <li><span className="tl-dot tl-dot-on" /> Sale created <em>{relativeTime(order.created_at)}</em></li>
            <li><span className="tl-dot tl-dot-on" /> Funds reserved <em>{relativeTime(order.created_at)}</em></li>
            <li><span className={`tl-dot ${order.status === "SUCCESS" || order.status === "FAILED" ? "tl-dot-on" : "tl-dot-wait"}`} /> Provider response <em>{order.status === "PENDING" ? "waiting" : order.completed_at ? relativeTime(order.completed_at) : "—"}</em></li>
            <li><span className={`tl-dot ${order.status === "SUCCESS" ? "tl-dot-on" : ""}`} /> Commission credited <em>{order.status === "SUCCESS" && order.completed_at ? relativeTime(order.completed_at) : "—"}</em></li>
          </ol>
        </div>

        <footer className="drawer-foot">
          {pending && (
            <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={onRefresh}>
              <RefreshCw size={14} /> Check status
            </button>
          )}
          {order.can_dispute && <Link to="/support" className="ev-btn ev-btn-sm ev-btn-secondary">Raise a dispute</Link>}
          {order.can_repeat && <Link to="/sell" className="ev-btn ev-btn-sm ev-btn-primary">Repeat sale</Link>}
          <Link to="/support" className="ev-btn ev-btn-sm ev-btn-ghost">Get help</Link>
        </footer>
      </aside>
    </div>
  );
}
